import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Container, Button } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { 
  detectDevice, 
  getRedirectUrl, 
  parseDeepLink, 
  generateAppDeepLink,
  generateAndroidIntentUrl,
  APP_URLS,
  DeepLinkInfo,
  DeviceType
} from '../../utils/deviceDetection';

/**
 * アプリダウンロード・ディープリンクページ
 * デバイスを判別して適切なストアまたはアプリにリダイレクト
 */
const AppRedirectPage: React.FC = () => {
  const [redirecting, setRedirecting] = useState(true);
  const [deviceType, setDeviceType] = useState<DeviceType>('other');
  const [deepLinkInfo, setDeepLinkInfo] = useState<DeepLinkInfo>({ type: 'none', id: null });
  const [appOpenAttempted, setAppOpenAttempted] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // デバイスを判別
    const device = detectDevice();
    setDeviceType(device);
    
    // URLパスからディープリンク情報を抽出
    const linkInfo = parseDeepLink(location.pathname);
    setDeepLinkInfo(linkInfo);
    
    // ディープリンクがある場合はアプリを開く試みをする
    if (linkInfo.type !== 'none' && linkInfo.id) {
      tryOpenApp(device, linkInfo);
    } else {
      // 単純なアプリダウンロードページ
      redirectToStore(device);
    }
  }, [location.pathname]);

  /**
   * アプリを開く試みをする
   */
  const tryOpenApp = (device: DeviceType, linkInfo: DeepLinkInfo) => {
    if (device === 'android') {
      // Android: Intent URLを使用
      const intentUrl = generateAndroidIntentUrl(linkInfo);
      window.location.href = intentUrl;
      setAppOpenAttempted(true);
      
      // 3秒後にまだページが表示されていれば、リダイレクト中の表示を解除
      setTimeout(() => {
        setRedirecting(false);
      }, 3000);
    } else if (device === 'ios') {
      // iOS: Universal Links を試み、失敗したらカスタムスキームを試す
      const appDeepLink = generateAppDeepLink(linkInfo);
      
      if (appDeepLink) {
        // まずカスタムスキームでアプリを開く試み
        const startTime = Date.now();
        window.location.href = appDeepLink;
        setAppOpenAttempted(true);
        
        // 2秒後にまだページが表示されていれば、App Storeにリダイレクト
        setTimeout(() => {
          // ページがまだアクティブなら、アプリが開かなかったとみなす
          if (Date.now() - startTime < 3000) {
            setRedirecting(false);
          }
        }, 2500);
      }
    } else {
      // その他のデバイス: 公式サイトにリダイレクト
      redirectToStore(device);
    }
  };

  /**
   * ストアにリダイレクト
   */
  const redirectToStore = (device: DeviceType) => {
    const redirectUrl = getRedirectUrl(device);
    
    // 1秒後にリダイレクト
    const timer = setTimeout(() => {
      window.location.href = redirectUrl;
    }, 1000);

    return () => clearTimeout(timer);
  };

  /**
   * 手動でストアに移動
   */
  const handleGoToStore = () => {
    const redirectUrl = getRedirectUrl(deviceType);
    window.location.href = redirectUrl;
  };

  const getDeviceDisplayName = (type: DeviceType): string => {
    switch (type) {
      case 'android':
        return 'Android';
      case 'ios':
        return 'iOS';
      default:
        return 'その他';
    }
  };

  const getDeepLinkDescription = (): string => {
    if (deepLinkInfo.type === 'product') {
      return '商品ページ';
    } else if (deepLinkInfo.type === 'profile') {
      return 'プロフィールページ';
    }
    return '';
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          gap: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          margin: -3,
          padding: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: 4,
            padding: 4,
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            アリクイ
          </Typography>
          
          {redirecting ? (
            <>
              <CircularProgress size={50} sx={{ my: 2 }} />
              <Typography variant="body1" color="text.secondary">
                {deviceType && `${getDeviceDisplayName(deviceType)}デバイスを検出しました`}
              </Typography>
              {deepLinkInfo.type !== 'none' && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {getDeepLinkDescription()}をアプリで開いています...
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {appOpenAttempted 
                  ? 'アプリを起動しています...'
                  : '適切なページにリダイレクトしています...'}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {deepLinkInfo.type !== 'none' 
                  ? 'アプリがインストールされていないようです。'
                  : 'アプリをダウンロードしてください。'}
              </Typography>
              
              <Button
                variant="contained"
                size="large"
                onClick={handleGoToStore}
                sx={{
                  mt: 2,
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                  },
                }}
              >
                {deviceType === 'ios' ? 'App Storeで開く' : 
                 deviceType === 'android' ? 'Google Playで開く' : 
                 '公式サイトへ'}
              </Button>
            </>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography variant="caption" color="text.secondary">
              {redirecting 
                ? '自動的にリダイレクトされない場合は、以下のリンクをクリックしてください：'
                : 'その他のリンク：'}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <a 
                href={APP_URLS.ANDROID_STORE}
                style={{ color: '#667eea', textDecoration: 'none', fontSize: '14px' }}
              >
                Android版アプリ（Google Play）
              </a>
              <a 
                href={APP_URLS.IOS_STORE}
                style={{ color: '#667eea', textDecoration: 'none', fontSize: '14px' }}
              >
                iOS版アプリ（App Store）
              </a>
              <a 
                href={APP_URLS.OFFICIAL_SITE}
                style={{ color: '#667eea', textDecoration: 'none', fontSize: '14px' }}
              >
                公式サイト
              </a>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default AppRedirectPage;
